import { Loader2, Plus, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { trpc } from "~/api/trpc";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "~/components/ui/select";

interface PmMapping {
    storeId: number | null;
    praxismanagerPractitionerId: string;
}

interface PractitionerExternalConfigFormProps {
    practitionerId: number;
}

export function PractitionerExternalConfigForm({ practitionerId }: PractitionerExternalConfigFormProps) {
    const utils = trpc.useUtils();

    const { data: practitionerData, isLoading: isLoadingPractitioner } = trpc.practitioners.getOne.useQuery(
        { id: practitionerId },
        { refetchOnWindowFocus: false }
    );

    const { data: storesData, isLoading: isLoadingStores } = trpc.stores.getAllSimple.useQuery(undefined, {
        refetchOnWindowFocus: false,
    });

    const [mappings, setMappings] = useState<PmMapping[]>([]);
    const [initialMappings, setInitialMappings] = useState<PmMapping[]>([]);

    useEffect(() => {
        if (practitionerData?.praxismanagerIds) {
            const loaded: PmMapping[] = practitionerData.praxismanagerIds.map((entry) => ({
                storeId: entry.storeId,
                praxismanagerPractitionerId: String(entry.praxismanagerPractitionerId),
            }));
            setMappings(loaded);
            setInitialMappings(loaded);
        }
    }, [practitionerData]);

    const updateMutation = trpc.practitioners.updatePraxismanagerIds.useMutation({
        onSuccess: async () => {
            toast.success("Praxismanager-Zuordnungen gespeichert!");
            await utils.practitioners.getOne.invalidate({ id: practitionerId });
        },
        onError: (error) => {
            toast.error(`Fehler beim Speichern: ${error.message}`);
        },
    });

    const practitionerStoreIds = useMemo(
        () => new Set(practitionerData?.storeIds ?? []),
        [practitionerData]
    );

    const availableStores = useMemo(
        () => (storesData ?? []).filter((s) => practitionerStoreIds.has(s.id)),
        [storesData, practitionerStoreIds]
    );

    const usedStoreIds = useMemo(
        () => new Set(mappings.map((m) => m.storeId).filter((id): id is number => id !== null)),
        [mappings]
    );

    const isDirty = useMemo(() => {
        if (mappings.length !== initialMappings.length) return true;
        return mappings.some((m, i) => {
            const init = initialMappings[i];
            return m.storeId !== init?.storeId || m.praxismanagerPractitionerId !== init?.praxismanagerPractitionerId;
        });
    }, [mappings, initialMappings]);

    const handleAddRow = () => {
        setMappings((prev) => [...prev, { storeId: null, praxismanagerPractitionerId: "" }]);
    };

    const handleRemoveRow = (index: number) => {
        setMappings((prev) => prev.filter((_, i) => i !== index));
    };

    const handleStoreChange = (index: number, storeId: string) => {
        setMappings((prev) =>
            prev.map((m, i) => (i === index ? { ...m, storeId: parseInt(storeId, 10) } : m))
        );
    };

    const handlePmIdChange = (index: number, value: string) => {
        setMappings((prev) =>
            prev.map((m, i) => (i === index ? { ...m, praxismanagerPractitionerId: value } : m))
        );
    };

    const validate = (): string | null => {
        for (let i = 0; i < mappings.length; i++) {
            const m = mappings[i];
            if (m.storeId === null) return `Zeile ${i + 1}: Bitte eine Filiale auswählen.`;
            const pmId = parseInt(m.praxismanagerPractitionerId, 10);
            if (isNaN(pmId) || pmId <= 0) return `Zeile ${i + 1}: Praxismanager-ID muss eine positive Zahl sein.`;
        }
        const storeIds = mappings.map((m) => m.storeId);
        const duplicates = storeIds.filter((id, i) => storeIds.indexOf(id) !== i);
        if (duplicates.length > 0) return "Jede Filiale darf nur einmal zugeordnet werden.";
        return null;
    };

    const handleSave = () => {
        const error = validate();
        if (error) {
            toast.error(error);
            return;
        }
        updateMutation.mutate({
            practitionerId,
            mappings: mappings.map((m) => ({
                storeId: m.storeId!,
                praxismanagerPractitionerId: parseInt(m.praxismanagerPractitionerId, 10),
            })),
        });
    };

    if (isLoadingPractitioner || isLoadingStores) {
        return (
            <Card>
                <CardContent className="flex items-center justify-center min-h-[200px]">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader>
                <CardTitle>Praxismanager-Zuordnungen</CardTitle>
                <p className="text-sm text-muted-foreground">
                    Verknüpfen Sie diesen Behandler mit Praxismanager-Behandler-IDs pro Filiale.
                </p>
            </CardHeader>
            <CardContent className="space-y-4">
                {mappings.length === 0 && (
                    <p className="text-sm text-muted-foreground py-4 text-center">
                        Keine Praxismanager-Zuordnungen vorhanden.
                    </p>
                )}

                {mappings.map((mapping, index) => (
                    <div key={index} className="flex items-end gap-3">
                        <div className="flex-1 space-y-1.5">
                            <Label>Filiale</Label>
                            <Select
                                value={mapping.storeId !== null ? String(mapping.storeId) : ""}
                                onValueChange={(val) => handleStoreChange(index, val)}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Filiale auswählen..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {availableStores.map((store) => (
                                        <SelectItem
                                            key={store.id}
                                            value={String(store.id)}
                                            disabled={usedStoreIds.has(store.id) && mapping.storeId !== store.id}
                                        >
                                            {store.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="flex-1 space-y-1.5">
                            <Label>Praxismanager-Behandler-ID</Label>
                            <Input
                                type="number"
                                min={1}
                                placeholder="z.B. 12345"
                                value={mapping.praxismanagerPractitionerId}
                                onChange={(e) => handlePmIdChange(index, e.target.value)}
                            />
                        </div>

                        <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive shrink-0"
                            onClick={() => handleRemoveRow(index)}
                        >
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    </div>
                ))}

                <Button variant="outline" size="sm" onClick={handleAddRow} className="mt-2">
                    <Plus className="mr-2 h-4 w-4" />
                    Zuordnung hinzufügen
                </Button>
            </CardContent>
            <CardFooter className="flex justify-end border-t pt-4">
                <Button
                    onClick={handleSave}
                    disabled={updateMutation.isPending || !isDirty}
                >
                    {updateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Speichern
                </Button>
            </CardFooter>
        </Card>
    );
}
